// Dự án: thông tin quản trị (trạng thái, thành viên, giai đoạn…) gắn với dự án bên module Công việc.
// Công việc của dự án lấy từ useWorkStore theo projectId — không lưu trùng ở đây.
import dayjs from 'dayjs';
import { MEMBERS, ME_ID, PROJECTS, memberOf, seedTasks, type WorkTask } from '../WorkList/data';
import { isClosed, isOverdue, ymd } from '../WorkList/flow';

export type ProjectStatus = 'waiting' | 'doing' | 'paused' | 'done' | 'cancelled';
export type ProgressMethod = 'count' | 'days';
export type ProjectAction = 'start' | 'pause' | 'resume' | 'finish' | 'cancel' | 'reopen';
export type ProjectActivityType = 'create' | 'status' | 'edit' | 'members' | 'phase' | 'link' | 'comment' | 'shift';

export interface ProjectActivity { id: string; at: string; by: string; type: ProjectActivityType; text?: string }
export interface Phase { id: string; name: string; startAt?: string; endAt?: string; note?: string }
export interface ProjectLink { id: string; name: string; url: string; by: string; at: string }

export interface ProjectInfo {
  id: string; // = Project.id bên WorkList
  code: string;
  status: ProjectStatus;
  ownerIds: string[]; // người quản trị: đổi trạng thái, thành viên, giai đoạn
  memberIds: string[];
  followerIds: string[];
  startAt?: string;
  endAt?: string;
  actualStartAt?: string;
  actualEndAt?: string;
  description?: string;
  progressMethod: ProgressMethod;
  syncTaskDates: boolean; // dời lịch dự án thì dời lịch các việc đang mở
  taskWithinProject: boolean; // thời gian công việc phải nằm trong thời gian dự án
  phases: Phase[];
  links: ProjectLink[];
  activity: ProjectActivity[];
}

export const PROJECT_STATUS: Record<ProjectStatus, { label: string; tag: string; dot: string }> = {
  waiting: { label: 'Chờ thực hiện', tag: 'default', dot: '#94a3b8' },
  doing: { label: 'Đang thực hiện', tag: 'processing', dot: '#3b82f6' },
  paused: { label: 'Tạm dừng', tag: 'warning', dot: '#f59e0b' },
  done: { label: 'Hoàn thành', tag: 'success', dot: '#22c55e' },
  cancelled: { label: 'Huỷ', tag: 'default', dot: '#64748b' },
};

export const PROJECT_ACTION_LABEL: Record<ProjectAction, string> = {
  start: 'Bắt đầu dự án',
  pause: 'Tạm dừng',
  resume: 'Tiếp tục',
  finish: 'Hoàn thành dự án',
  cancel: 'Huỷ dự án',
  reopen: 'Mở lại',
};

export const PROGRESS_METHODS: { value: ProgressMethod; label: string; hint: string }[] = [
  { value: 'count', label: 'Theo số công việc hoàn thành', hint: '10 việc, xong 4 → 40%' },
  { value: 'days', label: 'Theo tỷ trọng ngày thực hiện', hint: 'Việc kéo dài nhiều ngày chiếm tỷ trọng lớn hơn' },
];

export const isProjectClosed = (p: ProjectInfo) => p.status === 'done' || p.status === 'cancelled';

export const canManageProject = (p: ProjectInfo, me: string) => {
  if (p.ownerIds.includes(me)) return true;
  const m = memberOf(me);
  if (m?.manager === 'all') return true;
  const dept = PROJECTS.find(x => x.id === p.id)?.departmentId;
  return m?.manager === 'department' && m.departmentId === dept;
};

export function projectActions(p: ProjectInfo, me: string): ProjectAction[] {
  if (!canManageProject(p, me)) return [];
  switch (p.status) {
    case 'waiting': return ['start', 'cancel'];
    case 'doing': return ['finish', 'pause', 'cancel'];
    case 'paused': return ['resume', 'cancel'];
    default: return ['reopen'];
  }
}

/** Việc của dự án (bỏ việc đã huỷ — không tính tiến độ, không tính thống kê) */
export const projectTasks = (projectId: string, tasks: WorkTask[]) => tasks.filter(t => t.projectId === projectId && t.status !== 'cancelled');

export function finishBlocker(p: ProjectInfo, tasks: WorkTask[]): string | null {
  const open = projectTasks(p.id, tasks).filter(t => !isClosed(t)).length;
  return open ? `Còn ${open} công việc chưa đóng — hoàn thành hoặc huỷ trước khi đóng dự án` : null;
}

const taskDays = (t: WorkTask) => (t.startAt && t.dueAt ? Math.max(1, dayjs(t.dueAt).diff(t.startAt, 'day') + 1) : 1);

export function progressOf(p: ProjectInfo, tasks: WorkTask[]): number {
  if (p.status === 'done') return 100;
  const list = projectTasks(p.id, tasks).filter(t => t.status !== 'failed');
  if (!list.length) return 0;
  const w = p.progressMethod === 'days' ? taskDays : () => 1;
  const total = list.reduce((s, t) => s + w(t), 0);
  const done = list.filter(t => t.status === 'done').reduce((s, t) => s + w(t), 0);
  return Math.round((done / total) * 100);
}

export interface ProjectStats { total: number; todo: number; doing: number; review: number; overdue: number; done: number; late: number; failed: number }

export function statsOf(projectId: string, tasks: WorkTask[]): ProjectStats {
  const list = projectTasks(projectId, tasks);
  return {
    total: list.length,
    todo: list.filter(t => t.status === 'todo' && !isOverdue(t)).length,
    doing: list.filter(t => t.status === 'doing' && !isOverdue(t)).length,
    review: list.filter(t => t.status === 'review').length,
    overdue: list.filter(t => isOverdue(t)).length,
    done: list.filter(t => t.status === 'done').length,
    late: list.filter(t => t.status === 'done' && !!t.dueAt && !!t.doneAt && t.doneAt > t.dueAt).length,
    failed: list.filter(t => t.status === 'failed').length,
  };
}

/** Người tham gia = quản trị + thành viên + bất kỳ ai đang được giao việc trong dự án */
export function participantsOf(p: ProjectInfo, tasks: WorkTask[]): string[] {
  const ids = new Set([...p.ownerIds, ...p.memberIds]);
  projectTasks(p.id, tasks).forEach(t => { if (t.assigneeId) ids.add(t.assigneeId); });
  return [...ids];
}

/** Việc có lịch nằm ngoài thời gian dự án (khi bật quy tắc) */
export const outOfRange = (p: ProjectInfo, t: WorkTask) =>
  !isClosed(t) && ((!!p.startAt && !!t.startAt && t.startAt < p.startAt) || (!!p.endAt && !!t.dueAt && t.dueAt > p.endAt));

export interface StatusInput { by: string; note?: string; now?: string }

/** Chuyển trạng thái dự án; trả về chuỗi lỗi nếu không hợp lệ */
export function applyProjectAction(p: ProjectInfo, action: ProjectAction, tasks: WorkTask[], input: StatusInput): ProjectInfo | string {
  if (!projectActions(p, input.by).includes(action)) return 'Bạn không có quyền thực hiện thao tác này';
  const now = input.now ?? ymd();
  const log = (to: ProjectStatus) => [...p.activity, newProjectAct(input.by, 'status', `${PROJECT_STATUS[p.status].label} → ${PROJECT_STATUS[to].label}${input.note?.trim() ? ` · ${input.note.trim()}` : ''}`)];
  switch (action) {
    case 'start':
      return { ...p, status: 'doing', actualStartAt: p.actualStartAt ?? now, activity: log('doing') };
    case 'pause':
      if (!input.note?.trim()) return 'Cần ghi lý do tạm dừng';
      return { ...p, status: 'paused', activity: log('paused') };
    case 'resume':
      return { ...p, status: 'doing', activity: log('doing') };
    case 'finish': {
      const blocker = finishBlocker(p, tasks);
      if (blocker) return blocker;
      return { ...p, status: 'done', actualEndAt: now, activity: log('done') };
    }
    case 'cancel':
      if (!input.note?.trim()) return 'Cần ghi lý do huỷ dự án';
      return { ...p, status: 'cancelled', actualEndAt: now, activity: log('cancelled') };
    case 'reopen':
      return { ...p, status: 'doing', actualEndAt: undefined, activity: log('doing') };
    default:
      return p;
  }
}

export const newProjectAct = (by: string, type: ProjectActivityType, text?: string): ProjectActivity => ({
  id: `pa-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`, at: new Date().toISOString(), by, type, text,
});

export const PROJECT_ACTIVITY_TEXT: Record<ProjectActivityType, string> = {
  create: 'tạo dự án',
  status: 'đổi trạng thái',
  edit: 'sửa thông tin dự án',
  members: 'cập nhật thành viên',
  phase: 'cập nhật giai đoạn',
  link: 'thêm tài liệu',
  comment: 'thảo luận',
  shift: 'dời lịch dự án',
};

const SEED: Record<string, { status: ProjectStatus; description: string; phases: [string, number, number][] }> = {
  'p-hk': { status: 'doing', description: 'Tự rà soát giấy phép, chứng chỉ hành nghề, hồ sơ bệnh án, kiểm soát nhiễm khuẩn và xử lý chất thải y tế trước đợt kiểm tra của Sở Y tế.', phases: [['Tự rà soát hồ sơ', 0, 0.4], ['Khắc phục tồn tại', 0.35, 0.8], ['Diễn tập & nghiệm thu', 0.8, 1]] },
  'p-bf': { status: 'doing', description: 'Chương trình ưu đãi Black Friday: nội dung, quảng cáo và kịch bản tư vấn.', phases: [['Lên ý tưởng', 0, 0.3], ['Sản xuất nội dung', 0.3, 0.7], ['Chạy chiến dịch', 0.7, 1]] },
  'p-tele': { status: 'doing', description: 'Tổng hợp số cuộc gọi, lịch hẹn và tỷ lệ chuyển đổi của Telesales hằng tuần.', phases: [] },
  'p-lt': { status: 'doing', description: 'Chuẩn hoá các bước thực hiện liệu trình, checklist và biểu mẫu cho KTV.', phases: [['Khảo sát hiện trạng', 0, 0.35], ['Soạn quy trình', 0.35, 0.8], ['Áp dụng thử', 0.8, 1]] },
  'p-cms': { status: 'doing', description: 'Triển khai CMS v2 cho toàn hệ thống: dữ liệu, phân quyền, đào tạo người dùng.', phases: [['Phân tích', 0, 0.25], ['Phát triển', 0.25, 0.8], ['Nghiệm thu', 0.8, 1]] },
  'p-ktv': { status: 'paused', description: 'Đào tạo nâng cao tay nghề KTV quý 4.', phases: [] },
};

export function seedProjects(): ProjectInfo[] {
  const tasks = seedTasks();
  return PROJECTS.filter(p => p.type === 'project').map((p, i) => {
    const list = tasks.filter(t => t.projectId === p.id);
    const starts = list.map(t => t.startAt!).sort();
    const dues = list.map(t => t.dueAt).filter(Boolean).sort() as string[];
    const startAt = starts[0] ?? ymd();
    const endAt = dayjs(dues.at(-1) ?? dayjs().add(30, 'day')).add(7, 'day').format('YYYY-MM-DD');
    const deptMembers = MEMBERS.filter(m => m.departmentId === p.departmentId);
    const owner = deptMembers.find(m => m.manager === 'department')?.id ?? ME_ID;
    const seed = SEED[p.id];
    const span = dayjs(endAt).diff(startAt, 'day');
    const at = (k: number) => dayjs(startAt).add(Math.round(span * k), 'day').format('YYYY-MM-DD');
    return {
      id: p.id,
      code: `DA-${String(i + 1).padStart(3, '0')}`,
      status: seed?.status ?? 'doing',
      ownerIds: owner === ME_ID ? [ME_ID] : [owner, ME_ID],
      memberIds: deptMembers.map(m => m.id).filter(id => id !== owner && id !== ME_ID),
      followerIds: [],
      startAt,
      endAt,
      actualStartAt: startAt,
      description: seed?.description,
      progressMethod: 'count',
      syncTaskDates: true,
      taskWithinProject: false,
      phases: (seed?.phases ?? []).map(([name, a, b], k) => ({ id: `ph-${p.id}-${k}`, name, startAt: at(a), endAt: at(b) })),
      links: [],
      activity: [{ id: `pa-${p.id}-0`, at: dayjs(startAt).hour(8).toISOString(), by: owner, type: 'create' }],
    };
  });
}
